import { create } from 'zustand';

export const selectors = {
    // Фон
    backgrounds: (state) => state.backgrounds,
    currentBackgroundId: (state) => state.currentBackgroundId,
    currentBackground: (state) =>
        state.backgrounds.find(bg => bg.id === state.currentBackgroundId),
    rotation: (state) => state.rotation,
    flipX: (state) => state.flipX,
    flipY: (state) => state.flipY,

    // Текстовые блоки
    textBlocks: (state) => state.textBlocks,
    selectedTextBlock: (state) =>
        state.textBlocks.find(block => block.id === state.selectedBlockId),
    selectedBlockId: (state) => state.selectedBlockId,

    // CSV данные
    csvData: (state) => state.csvData,
    csvParsedData: (state) => state.csvParsedData,
    csvFileNameColumn: (state) => state.csvFileNameColumn,

    // Деривативные данные
    hasBackground: (state) => state.backgrounds.length > 0,
    hasCurrentBackground: (state) => state.currentBackgroundId !== null,
    textBlocksCount: (state) => state.textBlocks.length,
    hasSelection: (state) => state.selectedBlockId !== null,
    backgroundsCount: (state) => state.backgrounds.length,
    csvColumns: (state) => {
        if (!state.csvParsedData || state.csvParsedData.length === 0) return [];
        return Object.keys(state.csvParsedData[0]);
    },
};

// Хелперы для создания действий
export const actionCreators = {
    // Для фонов
    createBackground: (imageData, name = `Background ${new Date().toLocaleTimeString()}`) => ({
        id: Date.now().toString(),
        name,
        imageData,
        rotation: 0,
        flipX: false,
        flipY: false,
        createdAt: new Date().toISOString(),
    }),

    // Для текстовых блоков
    createTextBlock: (content = '', position = { x: 100, y: 100 }) => ({
        id: Date.now().toString(),
        key: `block_${Date.now()}`,
        content,
        position,
        fontSize: 16,
        color: '#000000',
        createdAt: new Date().toISOString(),
    }),

    // Для CSV данных
    createCSVRow: (data) => ({
        id: Date.now().toString(),
        ...data,
        importedAt: new Date().toISOString(),
    }),
};

const useStore = create((set, get) => ({
    // ===== Фон =====
    backgrounds: [],
    currentBackgroundId: null,
    rotation: 0,
    flipX: false,
    flipY: false,

    // ===== Текстовые блоки =====
    textBlocks: [],
    selectedBlockId: null,

    // ===== CSV данные =====
    csvData: [],
    csvFileNameColumn: null,
    csvParsedData: [],

    // ===== Действия с фоном =====
    // Добавление фона
    addBackground: (background) =>
        set((state) => ({
            backgrounds: [...state.backgrounds, background],
            currentBackgroundId: background.id,
            rotation: background.rotation || 0,
            flipX: background.flipX || false,
            flipY: background.flipY || false,
        })),

    addBackgroundFromImage: (imageData, name) => {
        const background = actionCreators.createBackground(imageData, name);
        return set((state) => ({
            backgrounds: [...state.backgrounds, background],
            currentBackgroundId: background.id,
            rotation: 0,
            flipX: false,
            flipY: false,
        }));
    },

    // Удаление фона
    removeBackground: (id) =>
        set((state) => {
            const newBackgrounds = state.backgrounds.filter(bg => bg.id !== id);
            let newCurrentId = state.currentBackgroundId;

            // Если удаляем текущий фон
            if (id === state.currentBackgroundId) {
                newCurrentId = newBackgrounds.length > 0 ? newBackgrounds[0].id : null;
            }

            return {
                backgrounds: newBackgrounds,
                currentBackgroundId: newCurrentId,
                // Сбрасываем трансформации если нет текущего фона
                ...(newCurrentId === null && {
                    rotation: 0,
                    flipX: false,
                    flipY: false,
                }),
            };
        }),

    // Выбор фона
    selectBackground: (id) =>
        set((state) => {
            const background = state.backgrounds.find(bg => bg.id === id);
            return {
                currentBackgroundId: id,
                rotation: background?.rotation || 0,
                flipX: background?.flipX || false,
                flipY: background?.flipY || false,
            };
        }),

    // Обновление фона
    updateBackground: (id, updates) =>
        set((state) => ({
            backgrounds: state.backgrounds.map(bg =>
                bg.id === id ? { ...bg, ...updates } : bg
            ),
            // Если обновляем текущий фон, обновляем и текущие значения
            ...(id === state.currentBackgroundId && {
                rotation: updates.rotation !== undefined ? updates.rotation : state.rotation,
                flipX: updates.flipX !== undefined ? updates.flipX : state.flipX,
                flipY: updates.flipY !== undefined ? updates.flipY : state.flipY,
            }),
        })),

    // Переключение между фонами
    nextBackground: () => {
        const state = get();
        if (state.backgrounds.length === 0) return;

        const currentIndex = state.backgrounds.findIndex(
            bg => bg.id === state.currentBackgroundId
        );
        const nextIndex = (currentIndex + 1) % state.backgrounds.length;
        const nextBackground = state.backgrounds[nextIndex];

        set({
            currentBackgroundId: nextBackground.id,
            rotation: nextBackground.rotation || 0,
            flipX: nextBackground.flipX || false,
            flipY: nextBackground.flipY || false,
        });
    },

    previousBackground: () => {
        const state = get();
        if (state.backgrounds.length === 0) return;

        const currentIndex = state.backgrounds.findIndex(
            bg => bg.id === state.currentBackgroundId
        );
        const prevIndex = currentIndex <= 0 ?
            state.backgrounds.length - 1 : currentIndex - 1;
        const prevBackground = state.backgrounds[prevIndex];

        set({
            currentBackgroundId: prevBackground.id,
            rotation: prevBackground.rotation || 0,
            flipX: prevBackground.flipX || false,
            flipY: prevBackground.flipY || false,
        });
    },

    // Управление трансформациями (применяется к текущему фону)
    setRotation: (rotation) =>
        set((state) => {
            if (state.currentBackgroundId) {
                return {
                    rotation,
                    backgrounds: state.backgrounds.map(bg =>
                        bg.id === state.currentBackgroundId
                            ? { ...bg, rotation }
                            : bg
                    ),
                };
            }
            return { rotation };
        }),

    toggleFlipX: () =>
        set((state) => {
            const newFlipX = !state.flipX;
            if (state.currentBackgroundId) {
                return {
                    flipX: newFlipX,
                    backgrounds: state.backgrounds.map(bg =>
                        bg.id === state.currentBackgroundId
                            ? { ...bg, flipX: newFlipX }
                            : bg
                    ),
                };
            }
            return { flipX: newFlipX };
        }),

    toggleFlipY: () =>
        set((state) => {
            const newFlipY = !state.flipY;
            if (state.currentBackgroundId) {
                return {
                    flipY: newFlipY,
                    backgrounds: state.backgrounds.map(bg =>
                        bg.id === state.currentBackgroundId
                            ? { ...bg, flipY: newFlipY }
                            : bg
                    ),
                };
            }
            return { flipY: newFlipY };
        }),

    rotateBackground: (degrees = 90) =>
        set((state) => {
            const newRotation = (state.rotation + degrees) % 360;
            if (state.currentBackgroundId) {
                return {
                    rotation: newRotation,
                    backgrounds: state.backgrounds.map(bg =>
                        bg.id === state.currentBackgroundId
                            ? { ...bg, rotation: newRotation }
                            : bg
                    ),
                };
            }
            return { rotation: newRotation };
        }),

    // Очистка
    clearCurrentBackground: () =>
        set((state) => {
            if (state.currentBackgroundId) {
                const newBackgrounds = state.backgrounds.filter(
                    bg => bg.id !== state.currentBackgroundId
                );
                const newCurrentId = newBackgrounds.length > 0 ? newBackgrounds[0].id : null;

                return {
                    backgrounds: newBackgrounds,
                    currentBackgroundId: newCurrentId,
                    rotation: 0,
                    flipX: false,
                    flipY: false,
                };
            }
            return {
                rotation: 0,
                flipX: false,
                flipY: false,
            };
        }),

    clearAllBackgrounds: () =>
        set({
            backgrounds: [],
            currentBackgroundId: null,
            rotation: 0,
            flipX: false,
            flipY: false,
        }),

    // Обратная совместимость (старые функции)
    setBackground: (imageData) => {
        const background = actionCreators.createBackground(imageData);
        return set({
            backgrounds: [background],
            currentBackgroundId: background.id,
            rotation: 0,
            flipX: false,
            flipY: false,
        });
    },

    clearBackground: () =>
        set({
            backgrounds: [],
            currentBackgroundId: null,
            rotation: 0,
            flipX: false,
            flipY: false,
        }),

    // ===== Действия с текстовыми блоками =====
    // Добавление
    addTextBlock: (block) =>
        set((state) => ({
            textBlocks: [...state.textBlocks, block],
            selectedBlockId: block.id
        })),

    addTextBlockWithDefaults: (content, position) => {
        const newBlock = actionCreators.createTextBlock(content, position);
        return set((state) => ({
            textBlocks: [...state.textBlocks, newBlock],
            selectedBlockId: newBlock.id
        }));
    },

    // Обновление
    updateTextBlock: (id, updates) =>
        set((state) => ({
            textBlocks: state.textBlocks.map(block =>
                block.id === id ? { ...block, ...updates } : block
            )
        })),

    updateTextBlockByKey: (key, updates) =>
        set((state) => ({
            textBlocks: state.textBlocks.map(block =>
                block.key === key ? { ...block, ...updates } : block
            )
        })),

    updateSelectedTextBlock: (updates) => {
        const state = get();
        if (state.selectedBlockId) {
            set({
                textBlocks: state.textBlocks.map(block =>
                    block.id === state.selectedBlockId ? { ...block, ...updates } : block
                )
            });
        }
    },

    // Удаление
    removeTextBlock: (id) =>
        set((state) => {
            const newBlocks = state.textBlocks.filter(block => block.id !== id);
            return {
                textBlocks: newBlocks,
                selectedBlockId: state.selectedBlockId === id ?
                    (newBlocks[0]?.id || null) : state.selectedBlockId
            };
        }),

    removeSelectedTextBlock: () => {
        const state = get();
        if (state.selectedBlockId) {
            set((state) => {
                const newBlocks = state.textBlocks.filter(
                    block => block.id !== state.selectedBlockId
                );
                return {
                    textBlocks: newBlocks,
                    selectedBlockId: newBlocks[0]?.id || null
                };
            });
        }
    },

    // Выбор
    selectTextBlock: (id) => set({ selectedBlockId: id }),
    clearSelection: () => set({ selectedBlockId: null }),
    selectNextTextBlock: () => {
        const state = get();
        if (state.textBlocks.length === 0) return;

        const currentIndex = state.textBlocks.findIndex(
            block => block.id === state.selectedBlockId
        );
        const nextIndex = (currentIndex + 1) % state.textBlocks.length;
        set({ selectedBlockId: state.textBlocks[nextIndex].id });
    },

    selectPreviousTextBlock: () => {
        const state = get();
        if (state.textBlocks.length === 0) return;

        const currentIndex = state.textBlocks.findIndex(
            block => block.id === state.selectedBlockId
        );
        const prevIndex = currentIndex <= 0 ?
            state.textBlocks.length - 1 : currentIndex - 1;
        set({ selectedBlockId: state.textBlocks[prevIndex].id });
    },

    // Массовые операции
    clearAllTextBlocks: () => set({ textBlocks: [], selectedBlockId: null }),
    importTextBlocks: (blocks) => set({ textBlocks: blocks }),

    // Позиционирование
    bringToFront: (id) => {
        const state = get();
        const blockIndex = state.textBlocks.findIndex(block => block.id === id);
        if (blockIndex === -1) return;

        const block = state.textBlocks[blockIndex];
        const otherBlocks = state.textBlocks.filter((_, idx) => idx !== blockIndex);

        set({
            textBlocks: [...otherBlocks, block],
            selectedBlockId: id
        });
    },

    sendToBack: (id) => {
        const state = get();
        const blockIndex = state.textBlocks.findIndex(block => block.id === id);
        if (blockIndex === -1) return;

        const block = state.textBlocks[blockIndex];
        const otherBlocks = state.textBlocks.filter((_, idx) => idx !== blockIndex);

        set({
            textBlocks: [block, ...otherBlocks],
            selectedBlockId: id
        });
    },

    // ===== Действия с CSV =====
    setCSVData: (data) => set({ csvData: data }),
    setCSVParsedData: (data) => set({ csvParsedData: data }),
    setCSVFileNameColumn: (columnName) => set({ csvFileNameColumn: columnName }),
    addCSVRow: (row) =>
        set((state) => ({
            csvData: [...state.csvData, actionCreators.createCSVRow(row)]
        })),
    clearCSVData: () => set({ csvData: [] }),
    cancelCSVImport: () => set({ csvParsedData: [] }),
    confirmCSVData: (selectedColumns = []) => {
        const state = get();
        if (!state.csvParsedData || state.csvParsedData.length === 0) {
            console.warn('Нет CSV данных для подтверждения')
            return []
        }

        const columnsToCreate = selectedColumns.length > 0
            ? selectedColumns
            : (state.csvParsedData[0] ? Object.keys(state.csvParsedData[0]) : [])

        const formattedData = state.csvParsedData.map((row, index) => ({
            id: Date.now().toString() + index,
            ...row,
            importedAt: new Date().toISOString(),
        }))

        const newBlocks = state.createTextBlocksFromCSV(state.csvParsedData, columnsToCreate);

        set({
            csvData: formattedData,
            csvParsedData: [],
            selectedBlockId: newBlocks && newBlocks.length > 0 ? newBlocks[0].id : state.selectedBlockId
        });

        return newBlocks || []
    },
    createTextBlocksFromCSV: (csvData, selectedColumns = []) => {
        const state = get()
        if (!csvData || csvData.length === 0) return []

        const firstRow = csvData[0]
        if (!firstRow) return []

        // Если переданы выбранные колонки, используем их
        // Иначе берем все колонки из CSV
        const columns = selectedColumns.length > 0
            ? selectedColumns
            : Object.keys(firstRow).filter(key =>
                key && key.trim() !== '' &&
                key !== 'id' &&
                key !== 'importedAt' &&
                key !== 'key'
            )

        if (columns.length === 0) return []

        const existingBlocks = state.textBlocks
        const existingKeys = new Set(existingBlocks.map(b => b.key))

        const newBlocks = []
        let yPosition = 50

        columns.forEach((column, index) => {
            // Пропускаем системные колонки
            if (column === 'id' || column === 'importedAt' || column === 'key') return

            // Если блок с таким ключом уже существует, пропускаем
            if (existingKeys.has(column)) {
                console.log(`Блок с ключом "${column}" уже существует, пропускаем`)
                return
            }

            const newBlock = {
                id: Date.now() + index,
                key: column,
                text: `{${column}}`,
                color: '#000000',
                fontSize: 16,
                fontFamily: 'Arial',
                fontWeight: 'normal',
                textAlign: 'left',
                x: 50,
                y: yPosition + (index * 60),
                width: 'auto',
                height: 'auto',
                createdAt: new Date().toISOString()
            }

            newBlocks.push(newBlock)
        })

        if (newBlocks.length > 0) {
            set((state) => ({
                textBlocks: [...state.textBlocks, ...newBlocks]
            }))

            return newBlocks
        }

        return [];
    },

    // ===== Универсальные действия =====
    resetStore: () => set({
        backgrounds: [],
        rotation: 0,
        flipX: false,
        flipY: false,
        textBlocks: [],
        csvData: [],
        selectedBlockId: null,
    }),

    // Для дебаггинга
    getStateSnapshot: () => get(),
}));

// Вспомогательная функция для подстановки данных в шаблон
export const applyCSVToTemplate = (template, csvRow) => {
    if (!template || !csvRow) return template;

    return template.replace(/\{(\w+)\}/g, (match, columnName) => {
        return csvRow[columnName] !== undefined ? csvRow[columnName] : match;
    });
};

// Вспомогательная функция для генерации всех вариантов открыток
export const generateAllPostcards = (backgrounds, textBlocks, csvData, csvFileNameColumn = null) => {
    if (!backgrounds || backgrounds.length === 0 || !csvData || csvData.length === 0) {
        return [];
    }

    const result = [];

    backgrounds.forEach((background, bgIndex) => {
        csvData.forEach((csvRow, rowIndex) => {
            const processedTextBlocks = textBlocks.map(block => ({
                ...block,
                content: applyCSVToTemplate(block.content, csvRow),
                key: applyCSVToTemplate(block.key, csvRow),
            }));

            let fileName = `postcard_${bgIndex + 1}_${rowIndex + 1}`;
            if (csvFileNameColumn && csvRow[csvFileNameColumn]) {
                const columnValue = String(csvRow[csvFileNameColumn]);
                fileName = columnValue
                    .replace(/[^\w\u0400-\u04FF\s\-]/gi, '_')
                    .replace(/\s+/g, '_')
                    .trim();

                if (!fileName) {
                    fileName = `postcard_${bgIndex + 1}_${rowIndex + 1}`;
                }
            }

            result.push({
                id: `${background.id}_${rowIndex}`,
                background,
                csvRow,
                textBlocks: processedTextBlocks,
                fileName: `${fileName}.png`,
            });
        });
    });

    return result;
};

// Хуки-помощники для удобства использования
export const useBackground = () => {
    const backgrounds = useStore(selectors.backgrounds);
    const currentBackground = useStore(selectors.currentBackground);
    const currentBackgroundId = useStore(selectors.currentBackgroundId);
    const rotation = useStore(selectors.rotation);
    const flipX = useStore(selectors.flipX);
    const flipY = useStore(selectors.flipY);
    const hasBackground = useStore(selectors.hasBackground);
    const hasCurrentBackground = useStore(selectors.hasCurrentBackground);
    const backgroundsCount = useStore(selectors.backgroundsCount);

    return {
        backgrounds,
        currentBackground,
        currentBackgroundId,
        rotation,
        flipX,
        flipY,
        hasBackground,
        hasCurrentBackground,
        backgroundsCount,
    };
};
export const useTextBlocks = () => {
    const textBlocks = useStore(selectors.textBlocks);
    const selectedBlock = useStore(selectors.selectedTextBlock);
    const selectedBlockId = useStore(selectors.selectedBlockId);
    const count = useStore(selectors.textBlocksCount);
    const hasSelection = useStore(selectors.hasSelection);

    return {
        textBlocks,
        selectedBlock,
        selectedBlockId,
        count,
        hasSelection,
    };
};

export const useCSVData = () => {
    const csvData = useStore(selectors.csvData);
    return { csvData };
};

export default useStore;